import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import LensRoundedIcon from '@mui/icons-material/LensRounded';
import AttachmentImage from 'components/AttachmentImage';
import VideoPlayer from 'components/VideoPlayer';
import PlayIcon from 'components/Icons/Play';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// The frame takes its size from the first item (measured image ratio, or the
// video player's fixed height) so switching slides never resizes the layout.
const Primary = styled('div')`
  position: relative;
  border-radius: 8px;
  overflow: hidden;

  ${({ ratio }) => ratio && css`aspect-ratio: ${ratio};`}
  ${({ fixedHeight }) => fixedHeight && css`height: 400px;`}
`;

const Slide = styled('div')`
  position: absolute;
  inset: 0;

  & > div {
    height: 100%;
    max-height: 100%;
  }

  && video {
    height: 100%;
  }
`;

const ArrowButton = styled(IconButton)`
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto 0.5rem;
  width: 2rem;
  height: 2rem;

  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  opacity: 0.7;

  transition: opacity 0.2s ease-in-out;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
    opacity: 1;
  }
`;

const ArrowButtonLeft = styled(ArrowButton)`
  left: 0;
`;

const ArrowButtonRight = styled(ArrowButton)`
  right: 0;
`;

const Indicators = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const indicatorConfig = {
  shouldForwardProp: (prop) => prop !== 'active',
};
const Indicator = styled(LensRoundedIcon, indicatorConfig)`
  width: 11px;
  height: 11px;
  padding: 1px;
  margin: 1px;
  color: #a8a8a8;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  ${({ active, theme }) => active && `color: ${theme.palette.primary.main};`}
`;

const Thumbs = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Thumb = styled('button')`
  position: relative;
  width: 64px;
  height: 64px;
  padding: 0;
  border: 2px solid ${({ selected, theme }) => (selected ? theme.palette.primary.main : 'transparent')};
  border-radius: 8px;
  overflow: hidden;
  background: #f2f2f2;
  cursor: pointer;

  ${({ selected }) => !selected && `
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  `}
`;

const ThumbImage = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ThumbVideo = styled('div')`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #252525;
  color: #fff;

  ${({ poster }) => poster && css`
    background-image: url(${poster});
    background-size: cover;
    background-position: center;
  `}
`;

const MediaViewer = ({ items }) => {
  const { t } = useTranslation('component');
  const [index, setIndex] = useState(0);
  const [ratio, setRatio] = useState(null);

  const list = items ?? [];
  const first = list[0];
  const firstIsVideo = first?.type === 'video';

  useEffect(() => {
    setIndex(0);
    setRatio(null);

    if (!first?.src || firstIsVideo) {
      return undefined;
    }

    const image = new Image();
    image.onload = () => setRatio(image.naturalWidth / image.naturalHeight);
    image.src = first.src;
    return () => { image.onload = null; };
  }, [first?.src, firstIsVideo]);

  const currentIndex = Math.min(index, list.length - 1);
  const current = list[currentIndex];

  if (!current) {
    return null;
  }

  const sized = firstIsVideo || !!ratio;
  const content = current.type === 'video'
    ? <VideoPlayer url={current.url} poster={current.poster} />
    : <AttachmentImage src={current.src} srcSet={current.srcSet} />;

  return (
    <Wrapper>
      <Primary ratio={!firstIsVideo ? ratio : null} fixedHeight={firstIsVideo}>
        {sized ? <Slide>{content}</Slide> : content}

        {currentIndex > 0 && (
          <ArrowButtonLeft size="small" aria-label={t('mediaViewer.previous')} onClick={() => setIndex(currentIndex - 1)}>
            <NavigateBeforeRoundedIcon />
          </ArrowButtonLeft>
        )}
        {currentIndex < list.length - 1 && (
          <ArrowButtonRight size="small" aria-label={t('mediaViewer.next')} onClick={() => setIndex(currentIndex + 1)}>
            <NavigateNextRoundedIcon />
          </ArrowButtonRight>
        )}
      </Primary>

      {list.length > 1 && (
        <Indicators>
          {list.map((item, itemIndex) => (
            <Indicator key={item.id ?? itemIndex} active={itemIndex === currentIndex} onClick={() => setIndex(itemIndex)} />
          ))}
        </Indicators>
      )}

      {list.length > 1 && (
        <Thumbs>
          {list.map((item, itemIndex) => (
            <Thumb
              key={item.id ?? itemIndex}
              type="button"
              aria-label={t('mediaViewer.showItem', { number: itemIndex + 1 })}
              selected={itemIndex === currentIndex}
              onClick={() => setIndex(itemIndex)}
            >
              {item.type === 'video'
                ? <ThumbVideo poster={item.poster}><PlayIcon fontSize="small" /></ThumbVideo>
                : <ThumbImage src={item.thumbSrc ?? item.src} alt="" />}
            </Thumb>
          ))}
        </Thumbs>
      )}
    </Wrapper>
  );
};

MediaViewer.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.any,
    type: PropTypes.string,
    src: PropTypes.string,
    srcSet: PropTypes.string,
    thumbSrc: PropTypes.string,
    url: PropTypes.string,
    poster: PropTypes.string,
    name: PropTypes.string,
  })),
};

export default MediaViewer;
